import { useState, useEffect, useCallback } from "react";
import { moduleService } from "../../../services/moduleService";

interface UseModuleConfigOptions {
    moduleName: string;
    autoLoad?: boolean;
}

interface UseModuleConfigReturn {
    // État
    configs: Record<string, string>;
    isLoading: boolean;
    error: string | null;
    isActive: boolean;
    
    // Méthodes
    loadConfigs: () => Promise<void>;
    getConfig: (key: string) => string | undefined;
    setConfig: (key: string, value: string) => Promise<void>;
    deleteConfig: (key: string) => Promise<void>;
    batchConfigs: (configs: Array<{ key: string; value: string }>) => Promise<void>;
    reset: () => void;
}

export default function useModuleConfig({ 
    moduleName, 
    autoLoad = true 
}: UseModuleConfigOptions): UseModuleConfigReturn {
    const [configs, setConfigs] = useState<Record<string, string>>({});
    const [isActive, setIsActive] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Charger les configurations
    const loadConfigs = useCallback(async () => {
        if (!moduleName) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await moduleService.getModuleConfigs(moduleName);
            setConfigs(response?.configs || {});
            setIsActive(response?.is_active);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Erreur lors du chargement des configurations');
            setConfigs({});
            setIsActive(false);
        } finally {
            setIsLoading(false);
        }
    }, [moduleName]);

    // Récupérer une config
    const getConfig = useCallback((key: string): string | undefined => {
        return configs[key];
    }, [configs]);

    // Définir une config
    const setConfig = useCallback(async (key: string, value: string) => {
        if (!moduleName) return;

        setIsLoading(true);
        setError(null);

        try {
            await moduleService.setModuleConfig(moduleName, key, value);
            // Recharger pour avoir la liste à jour
            await loadConfigs();
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Erreur lors de la mise à jour de la configuration');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [moduleName, loadConfigs]);

    // Supprimer une config
    const deleteConfig = useCallback(async (key: string) => {
        if (!moduleName) return;

        setIsLoading(true);
        setError(null);

        try {
            await moduleService.deleteModuleConfig(moduleName, key);
            // Recharger pour avoir la liste à jour
            await loadConfigs();
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Erreur lors de la suppression de la configuration');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [moduleName, loadConfigs]);

    // Mise à jour en batch
    const batchConfigs = useCallback(async (configs: Array<{ key: string; value: string }>) => {
        if (!moduleName) return;

        setIsLoading(true);
        setError(null);

        try {
            await moduleService.batchModuleConfigs(moduleName, configs);
            // Recharger pour avoir la liste à jour
            await loadConfigs();
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Erreur lors de la mise à jour des configurations');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [moduleName, loadConfigs]);

    // Reset
    const reset = useCallback(() => {
        setConfigs({});
        setIsActive(false);
        setIsLoading(false);
        setError(null);
    }, []);

    // Auto-load
    useEffect(() => {
        if (autoLoad && moduleName) {
            loadConfigs();
        }
    }, [autoLoad, moduleName, loadConfigs]);

    return {
        // État
        configs,
        isLoading,
        error,
        isActive,
        
        // Méthodes
        loadConfigs,
        getConfig,
        setConfig,
        deleteConfig,
        batchConfigs,
        reset,
    };
}